import { MessageComposition, ProviderTemplate, Template } from '../../../../../types/communication';
import { findInCatalog, providerPlaceholders } from '../../../../../utils/providerCatalog';
import { bodiesOf, envelopeOf, templateLabel, unusedVariableNames } from './studioDraft';

export type StudioSection = 'identity' | 'content' | 'delivery' | 'variables' | 'review';

export type IssueLevel = 'error' | 'warning';

export interface StudioIssue {
    section: StudioSection;
    level: IssueLevel;
    message: string;
    templateId?: string;
}

const identityIssues = (meta: MessageComposition): StudioIssue[] => {
    const issues: StudioIssue[] = [];
    if (!meta.name.trim()) issues.push({ section: 'identity', level: 'error', message: 'Dê um nome à composição.' });
    if (!meta.description.trim())
        issues.push({ section: 'identity', level: 'warning', message: 'Uma descrição ajuda os colegas a achar a composição.' });
    if (!meta.groupId && meta.channel !== 'INTERNAL_MESSAGE')
        issues.push({ section: 'identity', level: 'warning', message: 'Sem grupo: só administradores conseguirão operar esta composição.' });
    return issues;
};

const templateContentIssues = (
    template: Template,
    providerCatalog: ProviderTemplate[],
    catalogAvailable: boolean,
): StudioIssue[] => {
    const at = { section: 'content' as const, templateId: template.id };
    if (template.channel === 'EXTERNAL_MESSAGE') {
        if (!catalogAvailable) {
            return [{ ...at, level: 'warning', message: `${template.name}: catálogo de modelos do WhatsApp indisponível; não foi possível validar o modelo aprovado.` }];
        }
        const provider = findInCatalog(providerCatalog, template.providerTemplateName);
        if (!provider) return [{ ...at, level: 'error', message: `${template.name}: escolha um modelo aprovado no WhatsApp.` }];
        const bound = new Map((template.providerBindings ?? []).map((b) => [b.placeholder, b.variable]));
        const missing = providerPlaceholders(provider).filter((p) => !bound.get(p));
        return missing.length
            ? [{ ...at, level: 'error', message: `${template.name}: ligue ${missing.map((p) => (p === 'documento' ? 'o documento' : `{{${p}}}`)).join(', ')} a uma variável.` }]
            : [];
    }
    const issues: StudioIssue[] = [];
    const empty = template.editorMode === 'BLOCKS' ? !(template.blocks ?? []).length : !template.content.trim();
    if (empty) issues.push({ ...at, level: 'error', message: `${template.name}: o conteúdo está vazio.` });
    return issues;
};

const deliveryIssues = (meta: MessageComposition, templates: Template[]): StudioIssue[] => {
    const config = meta.channelConfig;
    const issue = (level: IssueLevel, message: string): StudioIssue => ({ section: 'delivery', level, message });
    if (config.kind === 'EMAIL') {
        const envelopeTemplate = envelopeOf(templates);
        const e = envelopeTemplate?.envelope;
        if (!envelopeTemplate || !e) return [issue('error', 'Falta o modelo de envio (assunto e destinatários).')];
        const at = (message: string): StudioIssue => ({ section: 'delivery', level: 'error', message, templateId: envelopeTemplate.id });
        const issues: StudioIssue[] = [];
        // O destinatário do e-mail é fixo ({{EMAIL}}), resolvido a cada envio pela notificação:
        // não é um campo que o usuário informa, então não entra na validação de ativação.
        if (!e.subject.trim()) issues.push(at('Escreva o assunto do e-mail.'));
        if (!e.from.length) issues.push(at('Informe o remetente.'));
        if (e.attachments.some((a) => !a.source.trim())) issues.push(at('Há arquivo sem origem definida.'));
        return issues;
    }
    if (config.kind === 'INTERNAL_MESSAGE') {
        const issues: StudioIssue[] = [];
        if (config.variants.length === 0) {
            issues.push(issue('error', 'Informe ao menos o canal de produção do Slack.'));
            return issues;
        }
        if (config.variants.some((v) => !v.channel.trim())) {
            issues.push(issue('error', 'Há canal do Slack sem nome definido.'));
        }
        if (config.variants.some((v) => v.environments.length === 0)) {
            issues.push(issue('error', 'Há canal do Slack sem ambiente selecionado.'));
        }
        if (!config.variants.some((v) => v.environments.includes('prd'))) {
            issues.push(issue('error', 'Defina o canal de produção (PRD) do Slack.'));
        }
        return issues;
    }
    return config.to.length ? [] : [issue('error', 'Informe o telefone de destino.')];
};

const variableIssues = (meta: MessageComposition, template: Template): StudioIssue[] => {
    const at = { section: 'variables' as const, templateId: template.id };
    const issues: StudioIssue[] = [];
    template.variables.forEach((v) => {
        if (!v.label.trim()) issues.push({ ...at, level: 'error', message: `${v.name}: falta o rótulo.` });
    });
    const unused = unusedVariableNames(template, meta.channelConfig);
    if (unused.length)
        issues.push({ ...at, level: 'warning', message: `${templateLabel(template)}: ${unused.length} variável(is) declarada(s) e não usada(s).` });
    return issues;
};

export const computeIssues = (
    meta: MessageComposition,
    templates: Template[],
    providerCatalog: ProviderTemplate[] = [],
    catalogAvailable = true,
): StudioIssue[] => {
    const bodies = bodiesOf(templates);
    return [
        ...identityIssues(meta),
        ...(bodies.length
            ? bodies.flatMap((t) => templateContentIssues(t, providerCatalog, catalogAvailable))
            : [{ section: 'content' as const, level: 'error' as const, message: 'A composição precisa de ao menos um modelo de conteúdo.' }]),
        ...deliveryIssues(meta, templates),
        ...templates.flatMap((t) => variableIssues(meta, t)),
    ];
};

export const countBySection = (issues: StudioIssue[], section: StudioSection, level: IssueLevel): number =>
    issues.filter((i) => i.section === section && i.level === level).length;
